/**
 * Deterministic tidy-tree layout for mind map branches.
 *
 * The connection graph is a tree, and a tree can always be drawn without any
 * connection crossing another. Free positioning is deliberate in this product,
 * so routing has to cope with arbitrary geometry — but when the layout engine
 * is the one choosing a position, it should choose one that cannot produce a
 * crossing in the first place.
 *
 * The arrangement is a classic tidy tree: children occupy a column beside
 * their parent, each child reserving the full vertical extent of its own
 * subtree, and the column centred on the parent. Because sibling subtrees
 * never share vertical space and every connection stays within its own
 * subtree's band, no two connections can cross.
 *
 * Manually positioned (pinned) nodes are never moved. They anchor their band
 * in the column and unpinned siblings flow around them, so a hand-placed idea
 * keeps exactly where it was put without giving up structure elsewhere.
 */
import { DEFAULT_NODE_SIZE } from './nodeGeometryDefaults';

export const DEFAULT_TIDY_GAP = Object.freeze({ horizontal: 80, vertical: 24 });

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function sizeOf(node, nodeSizes) {
  const runtime = nodeSizes?.get?.(node?.id);
  const candidate = runtime || node?.layoutSize || node?.size || {};
  return {
    width: Math.max(1, finite(candidate.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(1, finite(candidate.height, DEFAULT_NODE_SIZE.height)),
  };
}

function childrenOf(nodeId, childrenByParent) {
  return childrenByParent.get(nodeId) || [];
}

/**
 * Children are arranged in their recorded order so the visual sequence matches
 * the document order used by export and keyboard navigation. Nodes predating
 * that metadata fall back to their current vertical position, which keeps an
 * imported map recognisable the first time it is tidied.
 */
function orderChildren(children, nodesById) {
  return [...children].sort((left, right) => {
    const leftNode = nodesById.get(left);
    const rightNode = nodesById.get(right);
    const leftOrder = finite(leftNode?.layout?.order, Number.POSITIVE_INFINITY);
    const rightOrder = finite(rightNode?.layout?.order, Number.POSITIVE_INFINITY);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return finite(leftNode?.position?.y, 0) - finite(rightNode?.position?.y, 0)
      || String(left).localeCompare(String(right));
  });
}

/**
 * Split a root's children between the two sides so the map fills the canvas
 * evenly. An explicit recorded side always wins; the rest are dealt out to
 * whichever side is lighter, keeping the result stable for equal input.
 */
export function assignRootSides(childIds, nodesById) {
  const left = [];
  const right = [];
  const undecided = [];

  childIds.forEach((id) => {
    const side = nodesById.get(id)?.layout?.side;
    if (side === 'left') left.push(id);
    else if (side === 'right') right.push(id);
    else undecided.push(id);
  });

  undecided.forEach((id) => {
    if (right.length <= left.length) right.push(id);
    else left.push(id);
  });

  return { left, right };
}

// A branch continues away from its own parent, including legacy maps without
// side metadata. Root balancing is reserved for the central idea.
function branchSides(childIds, root, nodesById, relationships, balance) {
  if (balance) return assignRootSides(childIds, nodesById);
  const parentId = relationships.find((edge) => edge.childId === root.id)?.parentId;
  const parent = nodesById.get(parentId);
  const side = parent
    ? (finite(root.position?.x) < finite(parent.position?.x) ? 'left' : 'right')
    : root.layout?.side;
  return side === 'left' ? { left: childIds, right: [] } : { left: [], right: childIds };
}

function descendantsOf(id, childrenByParent, result = new Set()) {
  if (result.has(id)) return result;
  result.add(id);
  childrenOf(id, childrenByParent).forEach((child) => descendantsOf(child, childrenByParent, result));
  return result;
}

function clearTop(start, height, minX, maxX, reserved, padding) {
  const bands = reserved
    .filter((rect) => rect.x < maxX && rect.x + rect.width > minX)
    .map((rect) => [rect.y - padding, rect.y + rect.height + padding])
    .sort((a, b) => a[0] - b[0]);
  let top = start;
  for (const [low, high] of bands) {
    if (top < high && top + height > low) top = high;
  }
  return top;
}

function isPinned(node) {
  return node?.layout?.pinned === true;
}

/**
 * The children that take part in the column. A manually placed idea lives
 * wherever it was put, so it neither occupies a slot nor holds one open — its
 * siblings close up as though it were not in the column at all.
 */
function flowingChildren(nodeId, context) {
  const { nodesById, childrenByParent } = context;
  return orderChildren(childrenOf(nodeId, childrenByParent), nodesById)
    .filter((childId) => !isPinned(nodesById.get(childId)));
}

function pinnedChildren(nodeId, context) {
  const { nodesById, childrenByParent } = context;
  return orderChildren(childrenOf(nodeId, childrenByParent), nodesById)
    .filter((childId) => isPinned(nodesById.get(childId)));
}

function subtreeExtent(nodeId, context) {
  const { nodesById, nodeSizes, gap, cache } = context;
  if (cache.has(nodeId)) return cache.get(nodeId);

  const node = nodesById.get(nodeId);
  const ownHeight = sizeOf(node, nodeSizes).height;
  const children = flowingChildren(nodeId, context);

  let childrenHeight = 0;
  children.forEach((childId, index) => {
    childrenHeight += subtreeExtent(childId, context);
    if (index > 0) childrenHeight += gap.vertical;
  });

  const extent = Math.max(ownHeight, childrenHeight);
  cache.set(nodeId, extent);
  return extent;
}

function rectOf(nodeId, context, out) {
  const node = context.nodesById.get(nodeId);
  const size = sizeOf(node, context.nodeSizes);
  const placedPosition = out.get(nodeId);
  return {
    x: placedPosition ? placedPosition.x : finite(node?.position?.x, 0),
    y: placedPosition ? placedPosition.y : finite(node?.position?.y, 0),
    width: size.width,
    height: size.height,
  };
}

/**
 * Lay a set of sibling subtrees out as one column, stepping over any space a
 * pinned idea already occupies. Without this the column closes up over a
 * hand-placed idea and drops a sibling on top of it.
 */
function flowColumn({ childIds, centreY, columnEdgeX, direction }, context, out) {
  const { nodesById, nodeSizes, gap } = context;
  if (childIds.length === 0) return;

  const extents = childIds.map((childId) => subtreeExtent(childId, context));
  const widths = childIds.map((childId) => sizeOf(nodesById.get(childId), nodeSizes).width);
  const widest = Math.max(...widths, 0);
  const columnMinX = direction > 0 ? columnEdgeX : columnEdgeX - widest;
  const columnMaxX = direction > 0 ? columnEdgeX + widest : columnEdgeX;

  const total = extents.reduce((sum, value) => sum + value, 0)
    + gap.vertical * Math.max(0, childIds.length - 1);
  let cursor = centreY - total / 2;

  childIds.forEach((childId, index) => {
    const extent = extents[index];
    cursor = clearTop(cursor, extent, columnMinX, columnMaxX, context.reserved, gap.vertical);
    const anchorX = direction > 0 ? columnEdgeX : columnEdgeX - widths[index];
    placeSubtree(
      childId,
      { x: anchorX, centreY: cursor + extent / 2, direction },
      context,
      out
    );
    cursor += extent + gap.vertical;
  });
}

function placeSubtree(nodeId, { x, centreY, direction }, context, out) {
  const { nodesById, nodeSizes, gap } = context;
  const node = nodesById.get(nodeId);
  const size = sizeOf(node, nodeSizes);
  const pinnedHere = isPinned(node);

  // A pinned node keeps exactly where it was put. Its descendants are still
  // arranged relative to it, so an idea moved by hand does not lose the
  // structure beneath it.
  const left = pinnedHere ? finite(node?.position?.x, x) : x;
  let top = pinnedHere
    ? finite(node?.position?.y, centreY - size.height / 2)
    : centreY - size.height / 2;

  if (!pinnedHere) {
    top = clearTop(top, size.height, left, left + size.width, context.reserved, gap.vertical);
    out.set(nodeId, { x: left, y: top });
    context.reserved.push({ x: left, y: top, ...size });
  }

  // Each pinned child anchors its own subtree where the user left it, and the
  // space it occupies is reserved so the column steps over it.
  pinnedChildren(nodeId, context).forEach((childId) => {
    placeSubtree(childId, { x: left, centreY, direction }, context, out);
  });

  const children = flowingChildren(nodeId, context);
  if (children.length === 0) return;

  flowColumn(
    {
      childIds: children,
      centreY: top + size.height / 2,
      columnEdgeX: direction > 0
        ? left + size.width + gap.horizontal
        : left - gap.horizontal,
      direction,
    },
    context,
    out
  );
}

/**
 * Arrange every descendant of `rootId` into a tidy tree.
 *
 * The root itself never moves — it anchors the branch so tidying keeps the map
 * where the reader last saw it. Returns only the nodes whose position changes,
 * so an unchanged branch produces an empty result and no document write.
 */
export function planTidyBranch({
  rootId,
  nodes = [],
  relationships = [],
  nodeSizes = null,
  gap = DEFAULT_TIDY_GAP,
  balanceRootSides = false,
} = {}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const root = nodesById.get(rootId);
  if (!root) return new Map();

  const childrenByParent = new Map();
  relationships.forEach((relationship) => {
    if (!nodesById.has(relationship.parentId) || !nodesById.has(relationship.childId)) return;
    if (!childrenByParent.has(relationship.parentId)) {
      childrenByParent.set(relationship.parentId, []);
    }
    childrenByParent.get(relationship.parentId).push(relationship.childId);
  });

  const context = {
    nodesById,
    childrenByParent,
    nodeSizes,
    gap,
    cache: new Map(),
    // Stationary and already placed ideas, including other branches.
    reserved: [],
  };
  const branchIds = descendantsOf(rootId, childrenByParent);
  context.reserved = nodes
    .filter((node) => node.id === rootId || !branchIds.has(node.id) || isPinned(node))
    .map((node) => rectOf(node.id, context, new Map()));
  const placed = new Map();
  const rootSize = sizeOf(root, nodeSizes);
  const rootLeft = finite(root.position?.x, 0);
  const rootTop = finite(root.position?.y, 0);
  const rootCentreY = rootTop + rootSize.height / 2;

  const allChildren = flowingChildren(rootId, context);
  const sides = branchSides(allChildren, root, nodesById, relationships, balanceRootSides);

  // Pinned root children stay where they are, with their own subtrees tidy.
  // Their footprint is reserved before anything is flowed.
  pinnedChildren(rootId, context).forEach((childId) => {
    const pinnedNode = nodesById.get(childId);
    const direction = finite(pinnedNode?.position?.x, 0) < rootLeft ? -1 : 1;
    placeSubtree(childId, { x: rootLeft, centreY: rootCentreY, direction }, context, placed);
  });
  const runSide = (childIds, direction) => {
    flowColumn(
      {
        childIds,
        centreY: rootCentreY,
        columnEdgeX: direction > 0
          ? rootLeft + rootSize.width + gap.horizontal
          : rootLeft - gap.horizontal,
        direction,
      },
      context,
      placed
    );
  };

  runSide(sides.right, 1);
  runSide(sides.left, -1);

  // Report only real movement so an already-tidy branch commits nothing.
  const moved = new Map();
  placed.forEach((position, id) => {
    const node = nodesById.get(id);
    if (
      Math.abs(finite(node?.position?.x, 0) - position.x) < 0.5
      && Math.abs(finite(node?.position?.y, 0) - position.y) < 0.5
    ) return;
    moved.set(id, position);
  });
  return moved;
}

/**
 * Re-flow the column a new child joins, without re-laying the branch beneath it.
 *
 * Adding an idea should keep its parent's children balanced, but it should not
 * reach into subtrees the user has already arranged. Each sibling therefore
 * moves as a rigid block: its slot is the full extent of its current subtree,
 * and everything inside keeps the shape it already had.
 *
 * Returns positions for every node that moves, including the new child.
 */
export function planChildColumnReflow({
  parentId,
  nodes = [],
  relationships = [],
  nodeSizes = null,
  gap = DEFAULT_TIDY_GAP,
  balanceRootSides = false,
} = {}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const parent = nodesById.get(parentId);
  if (!parent) return new Map();

  const childrenByParent = new Map();
  relationships.forEach((relationship) => {
    if (!nodesById.has(relationship.parentId) || !nodesById.has(relationship.childId)) return;
    if (!childrenByParent.has(relationship.parentId)) {
      childrenByParent.set(relationship.parentId, []);
    }
    childrenByParent.get(relationship.parentId).push(relationship.childId);
  });

  const subtreeIdsOf = (id) => [...descendantsOf(id, childrenByParent)];
  const boundsOf = (ids) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    ids.forEach((id) => {
      const node = nodesById.get(id);
      const size = sizeOf(node, nodeSizes);
      const x = finite(node?.position?.x, 0);
      const y = finite(node?.position?.y, 0);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + size.width);
      maxY = Math.max(maxY, y + size.height);
    });
    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  };

  const parentSize = sizeOf(parent, nodeSizes);
  const parentLeft = finite(parent.position?.x, 0);
  const parentTop = finite(parent.position?.y, 0);
  const parentCentreY = parentTop + parentSize.height / 2;

  const all = orderChildren(childrenOf(parentId, childrenByParent), nodesById);
  // A rigid sibling block containing a manual anchor cannot move at all.
  const flowing = all.filter((id) => !subtreeIdsOf(id).some((child) => isPinned(nodesById.get(child))));
  const movable = new Set(flowing.flatMap(subtreeIdsOf));
  const reserved = nodes.filter((node) => !movable.has(node.id)).map((node) => ({
    x: finite(node.position?.x), y: finite(node.position?.y), ...sizeOf(node, nodeSizes),
  }));
  const sides = branchSides(flowing, parent, nodesById, relationships, balanceRootSides);
  const moved = new Map();

  const runSide = (childIds, direction) => {
    if (childIds.length === 0) return;
    const blocks = childIds.map((id) => {
      const ids = subtreeIdsOf(id);
      return { id, ids, bounds: boundsOf(ids) };
    });
    const widest = Math.max(...blocks.map((block) => block.bounds.width), 0);
    const columnEdgeX = direction > 0
      ? parentLeft + parentSize.width + gap.horizontal
      : parentLeft - gap.horizontal;
    const columnMinX = direction > 0 ? columnEdgeX : columnEdgeX - widest;
    const columnMaxX = direction > 0 ? columnEdgeX + widest : columnEdgeX;
    const total = blocks.reduce((sum, block) => sum + block.bounds.height, 0)
      + gap.vertical * Math.max(0, blocks.length - 1);
    let cursor = parentCentreY - total / 2;

    blocks.forEach((block) => {
      cursor = clearTop(cursor, block.bounds.height, columnMinX, columnMaxX, reserved, gap.vertical);
      const targetLeft = direction > 0 ? columnEdgeX : columnEdgeX - block.bounds.width;
      const delta = {
        x: targetLeft - block.bounds.minX,
        y: cursor - block.bounds.minY,
      };
      if (Math.abs(delta.x) >= 0.5 || Math.abs(delta.y) >= 0.5) {
        block.ids.forEach((id) => {
          const node = nodesById.get(id);
          moved.set(id, {
            x: finite(node?.position?.x, 0) + delta.x,
            y: finite(node?.position?.y, 0) + delta.y,
          });
        });
      }
      reserved.push({ x: targetLeft, y: cursor, width: block.bounds.width, height: block.bounds.height });
      cursor += block.bounds.height + gap.vertical;
    });
  };

  runSide(sides.right, 1);
  runSide(sides.left, -1);
  return moved;
}

/**
 * Side metadata for each root child, so a tidied map records the arrangement
 * it was given and keeps it across later edits.
 */
export function planRootSideMetadata({ rootId, nodes = [], relationships = [] } = {}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const childIds = relationships
    .filter((relationship) => relationship.parentId === rootId)
    .map((relationship) => relationship.childId)
    .filter((id) => nodesById.has(id));
  const ordered = orderChildren(childIds, nodesById);
  const { left, right } = assignRootSides(ordered.filter((id) => !isPinned(nodesById.get(id))), nodesById);
  const metadata = new Map();
  left.forEach((id) => metadata.set(id, 'left'));
  right.forEach((id) => metadata.set(id, 'right'));
  ordered.filter((id) => isPinned(nodesById.get(id))).forEach((id) => {
    metadata.set(id, finite(nodesById.get(id).position?.x) < finite(nodesById.get(rootId)?.position?.x)
      ? 'left' : 'right');
  });
  return metadata;
}
